<template>
  <b-row>
    <b-col>
      <b-table
        :items="historicoFormatado"
        :fields="colunas"
        responsive
        hover
        show-empty
        empty-text="Nenhum registro de alteração encontrado"
        class="tabela-historico-alteracoes"
      >
        <template #cell(data)="{ value }">
          <div class="ml-50">
            {{ formatarData(value) }}
          </div>
        </template>
        <template #cell(usuario)="{ value }">
          <div class="ml-50">
            {{ value }}
          </div>
        </template>
        <template #cell(campo)="{ item }">
          <b-badge
            variant="light-success"
            class="badge-campo"
          >
            {{ item.campo }}
          </b-badge>
        </template>
        <template #cell(de)="{ value }">
          <div class="ml-50">
            {{ formatarValor(value) }}
          </div>
        </template>
        <template #cell(para)="{ value }">
          <div class="ml-50">
            {{ formatarValor(value) }}
          </div>
        </template>
      </b-table>
    </b-col>
  </b-row>
</template>

<script>
import colunas from './useColunas'

/**
 * Mapeamento de nomes técnicos de campos para nomes amigáveis de exibição.
 */
const mapeamentoCampos = {
  // Grupo Geral
  razaoSocial: 'Razão Social',
  nomeFantasia: 'Nome Fantasia',
  cadastroFederal: 'CNPJ/CPF',
  cadastroFederalTipo: 'Tipo de Inscrição',
  inscricaoEstadual: 'Inscrição Estadual',
  inscricaoMunicipal: 'Inscrição Municipal',
  naturezaJuridica: 'Natureza Jurídica',
  empresaRegime: 'Regime',
  situacao: 'Situação',
  dataSituacao: 'Data Situação',
  isMatriz: 'Matriz/Filial',
  ativa: 'Empresa Ativa',
  favorita: 'Empresa Favorita',
  certificadoDigital: 'Certificado Digital',
  responsavelLegal: 'Responsável Legal',
  dataNascimento: 'Data de Nascimento',
  dataInativa: 'Data de Inativação',
  matriz: 'Matriz',
  // CNAE
  cnaePrincipal: 'CNAE Principal',
  cnaeSecundario: 'CNAE Secundário',
  cnaesSecundarios: 'CNAE Secundário',
  // Endereço e Contato
  'endereco.cep': 'CEP',
  'endereco.logradouro': 'Logradouro',
  'endereco.numero': 'Número',
  'endereco.complemento': 'Complemento',
  'endereco.municipio': 'Município',
  'endereco.bairro': 'Bairro',
  'endereco.cidade': 'Cidade',
  'endereco.uf': 'UF',
  'endereco.email': 'E-mail',
  'endereco.emailAdicional': 'E-mail Adicional',
  'endereco.telefoneContato.telefone': 'Telefone',
  'endereco.telefoneContato.celular': 'Celular',
  telefone: 'Telefone',
  celular: 'Celular',
  // Contador
  'contador.nome': 'Contador',
  'contador.email': 'E-mail Contador',
  'contador.telefone': 'Telefone Contador',
  // Sócios (Quadro Societário)
  socio: 'Sócio',
  'socio.nome': 'Nome do Sócio',
  'socio.cpf': 'CPF do Sócio',
  'socio.qualificacao': 'Qualificação do Sócio',
  'socio.descricao': 'Qualificação do Sócio', // Mapeamento solicitado
  'socio.dataEntrada': 'Data Entrada Sócio',
  'socio.percentualCapital': 'Percentual Capital',
  socios: 'Sócios',
  // Configurações e-CAC
  'configuracaoEcac.responsavelLegal': 'Responsável Legal (e-CAC)',
  'configuracaoEcac.dctfWeb': 'Rotina DCTFWeb',
  'configuracaoEcac.simplesNacional': 'Rotina Simples Nacional',
  'configuracaoEcac.pertSn': 'Rotina PERT SN',
  'configuracaoEcac.relpSn': 'Rotina RELP SN',
  'configuracaoEcac.rfb': 'Rotina RFB',
  'configuracaoEcac.naoPrevidenciario': 'Rotina Não Previdenciário',
  'configuracaoEcac.simplificadoPrevidenciario': 'Rotina Simplificado Previdenciário',
  'configuracaoEcac.pgfn': 'Rotina PGFN',
  'configuracaoEcac.mei': 'Rotina MEI',
  'configuracaoEcac.pertMei': 'Rotina PERT MEI',
  'configuracaoEcac.relpMei': 'Rotina RELP MEI',
  'configuracaoEcac.das': 'Rotina DAS',
  'configuracaoEcac.sublimite': 'Rotina Sublimite',
  configuracaoEcac: 'Configuração e-CAC',
  // Senhas
  senha: 'Senha',
  'senha.tipo': 'Tipo de Senha',
  'senha.usuario': 'Usuário da Senha',
  'senha.observacao': 'Observação da Senha',
  gestaoSenha: 'Senha de Acesso',
}

/**
 * Paleta de cores para os badges dos campos.
 */
const coresCampos = [
  '#E9FFE7', // verde
]

export default {
  name: 'VisualizacaoHistoricoAlteracoes',
  props: {
    historico: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      colunas,
      mapaCoresCampos: {},
      indiceCor: 0,
    }
  },
  computed: {
    /**
     * Retorna o histórico dom os nomes dos campos traduzidos para exibição.
     */
    historicoFormatado() {
      return this.historico.map(item => {
        let campoFormatado = mapeamentoCampos[item.campo] || item.campo

        // Formatação dinâmica para senhas (ex: senha.federal -> Senha Federal)
        if (!mapeamentoCampos[item.campo] && item.campo.toLowerCase().startsWith('senha.')) {
          const tipo = item.campo.split('.')[1]
          if (tipo) {
            campoFormatado = `Senha ${tipo.charAt(0).toUpperCase() + tipo.slice(1)}`
          }
        }

        // Formatação dinâmica para sócios (ex: socio.nome (49) ou socio.descricao (Nome))
        if (!mapeamentoCampos[item.campo] && item.campo.toLowerCase().startsWith('socio.')) {
          // Captura o campo base e o conteúdo entre parênteses
          const match = item.campo.match(/^(socio\.[a-zA-Z0-9]+)(?:\s*\((.*)\))?$/)
          if (match) {
            const nomeCampoBase = match[1] // ex: socio.nome
            const infoExtra = match[2] // ex: 49 ou Rafael Prado
            const traducao = mapeamentoCampos[nomeCampoBase]
            if (traducao) {
              campoFormatado = traducao
              // Se tiver info extra e não for apenas um número pequeno (ID interno), exibe
              // Ou se o usuário preferir, exibimos sempre formatado
              if (infoExtra) {
                const isId = /^\d+$/.test(infoExtra)
                if (!isId) {
                  campoFormatado = `${traducao} (${infoExtra})`
                }
              }
            }
          }
        }

        // Formatação dinâmica para config.rotina (ex: config.rotina.ParcelamentoSimplificadoPrevidenciario)
        if (!mapeamentoCampos[item.campo] && item.campo.toLowerCase().startsWith('config.rotina.')) {
          const partes = item.campo.split('.')
          if (partes.length >= 3) {
            const nomeRotina = partes[2]
            // Separa PascalCase em palavras (ex: ParcelamentoSimplificado -> Parcelamento Simplificado)
            let nomeFormatado = nomeRotina.replace(/([A-Z])/g, ' $1').trim()

            // Ajustes de siglas e acentos comuns
            nomeFormatado = nomeFormatado
              .replace(/Previdenciario/g, 'Previdenciário')
              .replace(/Relp/gi, 'RELP')
              .replace(/Pert/gi, 'PERT')
              .replace(/Mei/gi, 'MEI')
              .replace(/Rfb/gi, 'RFB')
              .replace(/Pgfn/gi, 'PGFN')
              .replace(/Cnd/gi, 'CND')
              .replace(/Dctf/gi, 'DCTF')
              .replace(/Das/gi, 'DAS')
              .replace(/\bSn\b/gi, 'SN') // Simples Nacional sigla se vier isolada
              .replace(/Dctf Web/gi, 'DCTFWeb') // Junta de volta se separou erradamente

            campoFormatado = `Rotina ${nomeFormatado}`
          }
        }

        return {
          ...item,
          campoOriginal: item.campo,
          campo: campoFormatado,
        }
      })
    },
  },
  methods: {
    /**
     * Formata a data para o padrão brasileiro DD/MM/YYYY HH:MM:SS.
     * @param {string} dataString - Data em formato ISO ou similar.
     * @returns {string} - Data formatada.
     */
    formatarData(dataString) {
      if (!dataString) return ''
      try {
        const data = new Date(dataString)
        const dia = String(data.getDate()).padStart(2, '0')
        const mes = String(data.getMonth() + 1).padStart(2, '0')
        const ano = data.getFullYear()
        const hora = String(data.getHours()).padStart(2, '0')
        const minuto = String(data.getMinutes()).padStart(2, '0')
        const segundo = String(data.getSeconds()).padStart(2, '0')
        return `${dia}/${mes}/${ano} ${hora}:${minuto}:${segundo}`
      } catch {
        return dataString
      }
    },
    /**
     * Obtém uma cor consistente para cada campo (mesma cor sempre para o mesmo campo).
     * @param {string} campo - Nome do campo original.
     * @returns {string} - Cor hexadecimal.
     */
    obterCorCampo(campo) {
      if (!this.mapaCoresCampos[campo]) {
        this.mapaCoresCampos[campo] = coresCampos[this.indiceCor % coresCampos.length]
        this.indiceCor += 1
      }
      return this.mapaCoresCampos[campo]
    },
    /**
     * Formata valores para exibição, traduzindo booleanos, capitalizando texto
     * e mostrando 'Em branco' para valores vazios.
     * @param {any} valor - Valor a ser formatado.
     * @returns {string} - Valor formatado.
     */
    formatarValor(valor) {
      if (valor === null || valor === undefined || valor === '') {
        return 'Em branco'
      }
      if (valor === true || valor === 'True' || valor === 'true') {
        return 'Verdadeiro'
      }
      if (valor === false || valor === 'False' || valor === 'false') {
        return 'Falso'
      }
      if (typeof valor === 'string' && valor.length > 0) {
        return valor.charAt(0).toUpperCase() + valor.slice(1)
      }
      return valor
    },
  },
}
</script>

<style lang="scss" scoped>
.tabela-historico-alteracoes {
  th {
    background-color: #f8f8f8 !important;
    font-size: 0.757rem !important;
    padding: 0.5rem 0.2rem;
    text-transform: uppercase;
    font-weight: 600;
    color: #5e5873;
  }

  td {
    padding: 0.5rem 0.2rem;
    font-size: 0.857rem !important;
    vertical-align: middle;
  }
}

.badge-campo {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.4em 0.8em;
  border-radius: 4px;
}
</style>
